import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, FilePlus2, Home } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { createTakstAssignment } from "@/lib/takst-api";
import { NewAssignmentDialog } from "@/components/takst/NewAssignmentDialog";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/takst/")({
  head: () => ({
    meta: [
      { title: "Takstoppdrag | VERQO" },
      {
        name: "description",
        content: "Takstoppdrag, befaring og tilstandsvurdering etter ARKAT/NS 3600 i VERQO.",
      },
      { property: "og:title", content: "Takstoppdrag | VERQO" },
      {
        property: "og:description",
        content: "Takstoppdrag, befaring og tilstandsvurdering etter ARKAT/NS 3600 i VERQO.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TakstIndexPage,
});

const WORKFLOW_LABEL: Record<string, string> = {
  draft: "Kladd",
  inspection_in_progress: "Befaring pågår",
  quality_review: "Kvalitetssikring",
  issued: "Utstedt",
  superseded: "Erstattet",
  withdrawn: "Trukket",
};

function TakstIndexPage() {
  const { companyId, user, activeModules } = useActiveCompany();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const moduleActive = activeModules.includes("building_surveyor");

  const listQ = useQuery({
    queryKey: ["takst-assignments", companyId],
    enabled: Boolean(companyId) && moduleActive,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("takst_assignments")
        .select("id, title, status, created_at, project_id, customer_id")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const assignments = data ?? [];
      if (assignments.length === 0) return [];
      const ids = assignments.map((a) => a.id);
      const [{ data: props }, { data: reports }, { data: customers }] = await Promise.all([
        supabase
          .from("takst_properties")
          .select("assignment_id, address, postal_code, city, cadastre_gnr, cadastre_bnr")
          .in("assignment_id", ids),
        supabase
          .from("takst_reports")
          .select("assignment_id, id, workflow_state, status, revision")
          .in("assignment_id", ids),
        supabase.from("customers").select("id, name").eq("company_id", companyId),
      ]);
      const propByAssignment = new Map((props ?? []).map((p) => [p.assignment_id, p]));
      const reportByAssignment = new Map((reports ?? []).map((r) => [r.assignment_id, r]));
      const customerById = new Map((customers ?? []).map((c) => [c.id, c.name]));
      return assignments.map((a) => ({
        ...a,
        property: propByAssignment.get(a.id) ?? null,
        report: reportByAssignment.get(a.id) ?? null,
        customerName: customerById.get(a.customer_id) ?? "—",
      }));
    },
  });

  const customersQ = useQuery({
    queryKey: ["takst-customers", companyId],
    enabled: Boolean(companyId) && moduleActive,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, name, address, postal_code, city")
        .eq("company_id", companyId)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const projectsQ = useQuery({
    queryKey: ["takst-projects", companyId],
    enabled: Boolean(companyId) && moduleActive,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number, customer_id, module_key")
        .eq("company_id", companyId)
        .eq("module_key", "building_surveyor")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const createM = useMutation({
    mutationFn: createTakstAssignment,
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ["takst-assignments", companyId] });
      setOpen(false);
      toast.success("Takstoppdrag opprettet");
      navigate({ to: "/takst/$id", params: { id: result.assignment_id } });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Kunne ikke opprette"),
  });

  const rows = useMemo(() => listQ.data ?? [], [listQ.data]);

  if (!moduleActive) {
    return (
      <>
        <PageHeader title="Takst" subtitle="Takstmann / bygningssakkyndig (ARKAT)" />
        <Card className="p-6">
          <h2 className="text-base font-semibold">Takst er ikke aktivert for bedriften</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Modulen <span className="font-medium">Takst</span> finnes i VERQO, men er ikke slått på
            for denne bedriften. Ingen takstdata går tapt – aktiver modulen under Innstillinger, så
            blir takstoppdrag, befaring og tilstandsvurdering tilgjengelig her.
          </p>
          <Button asChild className="mt-4" variant="outline">
            <Link to="/settings">Gå til innstillinger</Link>
          </Button>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Takstoppdrag"
        subtitle="Befaring og tilstandsvurdering etter NS 3600 (ARKAT)"
        action={
          <Button onClick={() => setOpen(true)}>
            <FilePlus2 className="h-4 w-4" /> Nytt takstoppdrag
          </Button>
        }
      />

      {listQ.isLoading ? (
        <div className="py-20 text-center text-sm text-muted-foreground">Laster…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="Ingen takstoppdrag ennå"
          description="Opprett et takstoppdrag eller start takst fra et eksisterende prosjekt."
          action={<Button onClick={() => setOpen(true)}>Nytt takstoppdrag</Button>}
        />

      ) : (
        <div className="card overflow-hidden">
          <div className="hidden grid-cols-[1fr,1fr,140px,140px] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
            <span>Eiendom</span>
            <span>Oppdragsgiver</span>
            <span>Opprettet</span>
            <span>Status</span>
          </div>
          {rows.map((row) => (
            <Link
              key={row.id}
              to="/takst/$id"
              params={{ id: row.id }}
              className="grid gap-1 border-b px-4 py-3 last:border-0 hover:bg-muted/30 md:grid-cols-[1fr,1fr,140px,140px] md:items-center md:gap-3"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2 truncate text-sm font-medium">
                  <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {row.property?.address ?? row.title}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {[row.property?.postal_code, row.property?.city].filter(Boolean).join(" ")}
                  {row.property?.cadastre_gnr
                    ? ` · gnr ${row.property.cadastre_gnr}/${row.property.cadastre_bnr ?? "–"}`
                    : ""}
                </span>
              </span>
              <span className="truncate text-sm">{row.customerName}</span>
              <span className="text-sm text-muted-foreground">{formatDate(row.created_at)}</span>
              <span className="chip w-fit">
                {WORKFLOW_LABEL[row.report?.workflow_state ?? "draft"] ?? "Kladd"}
              </span>
            </Link>
          ))}
        </div>
      )}

      <NewAssignmentDialog
        open={open}
        onOpenChange={setOpen}
        customers={customersQ.data ?? []}
        projects={projectsQ.data ?? []}
        saving={createM.isPending}
        onSubmit={(values) => {
          if (!companyId || !user?.id) {
            toast.error("Mangler bedrift eller bruker");
            return;
          }
          createM.mutate({ ...values, companyId, actorUserId: user.id });
        }}
      />
    </>
  );
}
